
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface ProductDetailHeroProps {
  product: {
    id: number;
    title: string;
    category: string;
    brand: string;
    model: string;
    certification: string;
    status: string;
    image: string;
    description: string;
  };
}

export default function ProductDetailHero({ product }: ProductDetailHeroProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <a href="/" className="hover:text-red-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/products" className="hover:text-red-600 transition-colors">Products</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">{product.title}</span>
          </div>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Product Image */}
          <div className="relative">
            <div className="bg-gray-50 rounded-2xl overflow-hidden aspect-square">
              <img 
                src={product.image}
                alt={product.title}
                className={`w-full h-full object-cover object-top transition-opacity duration-500 ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
              />
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                </div>
              )}
            </div>
          </div>

          {/* Product Summary */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {product.title}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Product Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Category</div>
                <div className="font-semibold text-gray-900">{product.category}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Brand</div>
                <div className="font-semibold text-gray-900">{product.brand}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Model</div>
                <div className="font-semibold text-gray-900">{product.model}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Status</div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="font-semibold text-green-600">{product.status}</span>
                </div>
              </div>
            </div>

            {/* Certification Badge */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="ri-shield-check-line text-green-600 text-xl mr-3"></i>
                <div>
                  <div className="font-semibold text-green-800">{product.certification}</div>
                  <div className="text-sm text-green-600">Certified for Qatar Standards</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="primary" className="flex-1">
                <i className="ri-message-3-line mr-2"></i>
                Request Quote
              </Button>
              <Button variant="outline" className="flex-1">
                <i className="ri-download-line mr-2"></i>
                Download Datasheet
              </Button>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
              <span className="text-sm text-gray-600">Share:</span>
              <button className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
                <i className="ri-linkedin-fill"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors">
                <i className="ri-whatsapp-line"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors">
                <i className="ri-mail-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
